/**
 * MODULE: _shared/lib/frontend/api/crmContractsApi.ts
 * PURPOSE: HTTP client for CRM commercial contracts (/api/v1/crm/contracts)
 * PUBLIC_API: crmListContracts, crmCreateContract, crmPatchContract, crmDeleteContract, CrmContract
 * SIDE_EFFECTS: fetch to API gateway
 * AGENT_SAFETY: RESTRICTED
 */

import { API_CONFIG, getAuthHeaders } from './config';

const CONTRACTS_BASE = `${API_CONFIG.baseURL}/api/v1/crm/contracts`;

export type CrmContractStatus = 'draft' | 'sent' | 'active' | 'expired' | 'cancelled';

export type CrmContract = {
  id: string;
  accountId: string;
  primaryContactId?: string | null;
  title: string;
  status: CrmContractStatus;
  value?: number | null;
  currencyCode: string;
  startDateISO?: string | null;
  endDateISO?: string | null;
  renewalDateISO?: string | null;
  renewalNoticeDays: number;
  autoRenew: boolean;
  documentUrl?: string | null;
  createdAtISO?: string;
  updatedAtISO?: string;
};

export type CrmContractListResponse = { items: CrmContract[]; total: number };

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function crmListContracts(params?: {
  accountId?: string;
  expiringWithinDays?: number;
  limit?: number;
  offset?: number;
}): Promise<CrmContractListResponse> {
  const q = new URLSearchParams();
  if (params?.accountId) q.set('accountId', params.accountId);
  if (params?.expiringWithinDays != null) q.set('expiringWithinDays', String(params.expiringWithinDays));
  if (params?.limit != null) q.set('limit', String(params.limit));
  if (params?.offset != null) q.set('offset', String(params.offset));
  const qs = q.toString();
  const url = qs ? `${CONTRACTS_BASE}?${qs}` : CONTRACTS_BASE;
  const res = await fetch(url, { headers: getAuthHeaders() });
  return parseJson<CrmContractListResponse>(res);
}

export async function crmCreateContract(body: {
  accountId: string;
  title: string;
  status?: CrmContractStatus;
  value?: number;
  currencyCode?: string;
  renewalDateISO?: string;
  endDateISO?: string;
  startDateISO?: string;
  autoRenew?: boolean;
}): Promise<CrmContract> {
  const res = await fetch(CONTRACTS_BASE, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseJson<CrmContract>(res);
}

export async function crmPatchContract(
  contractId: string,
  body: Partial<Omit<CrmContract, 'id' | 'accountId' | 'createdAtISO' | 'updatedAtISO'>>
): Promise<CrmContract> {
  const res = await fetch(`${CONTRACTS_BASE}/${contractId}`, {
    method: 'PATCH',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseJson<CrmContract>(res);
}

export async function crmDeleteContract(contractId: string): Promise<void> {
  const res = await fetch(`${CONTRACTS_BASE}/${contractId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  await parseJson<void>(res);
}
